import { useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import MovementScreen from "../../components/movement-screen/movement-screen";
import LifestyleScreen from "../../components/lifestyle-screen/lifestyle-screen";
import AssessmentSettings from "../../models/interfaces/assessment-settings";
import { AssessmentUtils } from "../../utilities/assessments/assessment-utils";
import BaseCardLayout from "../../components/base-card-layout/base-card-layout";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { AssessmentsUserMenu } from "../../components/assessments-user-menu/assessments-user-menu";
import { BackControllerProvider, useBackController } from "../../components/back-controller/back-controller";
import { NavigationDirectionProvider, useNavigationDirection } from "../../components/navigation-direction-context/navigation-direction-content";
import { MskScoreLite } from "../../models/interfaces/scores/msk-score-lite";
import { UserInfo } from "../../models/interfaces/scores/user-info";
import UserUtilAssessments from "../../utilities/user-util-assessments";
import CalculateMskLiteService from "../../utilities/services/calculate/calculate-msk-lite-service";
import { QuestionResponse } from "../../models/interfaces/questions/question-response";
import { createLogger } from "../../utilities/logger";

const log = createLogger("MskLitePage");

const COMPONENT_CLASS = "p-msk-lite";

// Module-level storage for cumulative responses across phases
let mskLiteStoredResponses: {
    movement: QuestionResponse[];
    bodyComposition: QuestionResponse[];
    physicalActivity: QuestionResponse[];
    injuryHistory: QuestionResponse[];
} = {
    movement: [],
    bodyComposition: [],
    physicalActivity: [],
    injuryHistory: [],
};

type MskLitePhase = "movement" | "bodyComposition" | "physicalActivity" | "injuryHistory" | "finished";

interface MSKLiteInnerProps {
    userId: string;
    phase: MskLitePhase;
    onPhaseChange: (phase: MskLitePhase) => void;
    currentScore: MskScoreLite | null;
    onScoreUpdate: (score: MskScoreLite) => void;
}

const MSKLiteInner: React.FC<MSKLiteInnerProps> = ({ userId, phase, onPhaseChange, currentScore, onScoreUpdate }) => {
    const history = useHistory();

    // Store settings per phase - keyed by phase name
    const [settingsByPhase, setSettingsByPhase] = useState<Record<string, AssessmentSettings>>({});

    const getSettings = (): AssessmentSettings => {
        const s = settingsByPhase[phase] || {
            completedGroupIds: [],
            currentSlideIndex: 0,
            currentGroupIndex: 0,
            questionResponses: [],
            slideGroups: [],
        };
        log.debug("[MSKLite] getSettings for phase", { phase, hasSettings: !!settingsByPhase[phase], slideGroupCount: s.slideGroups?.length });
        return s;
    };

    const updateSettings = (newSettings: AssessmentSettings) => {
        setSettingsByPhase(prev => ({ ...prev, [phase]: newSettings }));
    };

    const settings = getSettings();

    const { setDirection } = useNavigationDirection();
    const { invoke } = useBackController();
    const { state: authState } = useAuthState();

    const [userInfo, setUserInfo] = useState<UserInfo>();

    useEffect(() => {
        document.body.classList.add("-nav-hidden");
        return () => document.body.classList.remove("-nav-hidden");
    }, []);

    useEffect(() => {
        if (!authState.user || !authState.user.id) return;
        UserUtilAssessments.getUserInfo(authState.user.id).then((info) => {
            setUserInfo(info);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState.user?.id]);

    const onBackButtonClick = () => {
        log.debug("[MSKLite] Back button clicked");
        if (invoke()) return;
        setDirection("backward");
        if (AssessmentUtils.isFirstSlideAndFirstGroup(settings)) {
            history.goBack();
            return;
        }
        updateSettings(AssessmentUtils.moveToPreviousPage(settings));
    };

    // Called when movement screen submits
    const handleMovementSubmit = async () => {
        if (!userInfo) {
            log.warn("[MSKLite] Missing userInfo", {
                missing: { userInfo: true },
            });
            return;
        }

        const responses = AssessmentUtils.getAllResponses(settings);
        mskLiteStoredResponses.movement = responses;

        try {
            const result = await CalculateMskLiteService.score({
                userInfo,
                existingMskScoreLite: currentScore ?? undefined,
                movementResponses: responses,
                bodyCompositionResponses: [],
                physicalActivityResponses: [],
                injuryHistoryResponses: [],
            });

            log.info("MSK lite movement score calculated", {
                phase,
                mskScoreId: result?.id,
            });
            onScoreUpdate(result);
            onPhaseChange("bodyComposition");
        } catch (e) {
            log.error("Failed to score MSK lite movement assessment", {
                exception: e,
                phase,
            });
        }
    };

    // Called when lifestyle screen submits
    const handleLifestyleSubmit = async (lifestylePhase: "bodyComposition" | "physicalActivity" | "injuryHistory") => {
        log.debug("[MSKLite] handleLifestyleSubmit called", { lifestylePhase });
        if (!userInfo) {
            log.warn("[MSKLite] Missing userInfo", {
                missing: { userInfo: true },
            });
            return;
        }

        const responses = AssessmentUtils.getAllResponses(settings);
        log.debug("[MSKLite] responses", { responses });

        // Store responses for the appropriate phase
        if (lifestylePhase === "bodyComposition") {
            mskLiteStoredResponses.bodyComposition = responses;
        } else if (lifestylePhase === "physicalActivity") {
            mskLiteStoredResponses.physicalActivity = responses;
        } else if (lifestylePhase === "injuryHistory") {
            mskLiteStoredResponses.injuryHistory = responses;
        }

        // Determine next phase
        let nextPhase: MskLitePhase;
        if (lifestylePhase === "bodyComposition") {
            nextPhase = "physicalActivity";
        } else if (lifestylePhase === "physicalActivity") {
            nextPhase = "injuryHistory";
        } else {
            nextPhase = "finished";
        }

        try {
            const result = await CalculateMskLiteService.score({
                userInfo,
                existingMskScoreLite: currentScore ?? undefined,
                movementResponses: mskLiteStoredResponses.movement,
                bodyCompositionResponses: mskLiteStoredResponses.bodyComposition,
                physicalActivityResponses: mskLiteStoredResponses.physicalActivity,
                injuryHistoryResponses: mskLiteStoredResponses.injuryHistory,
            });

            log.info("MSK lite lifestyle score calculated", {
                lifestylePhase,
                mskScoreId: result?.id,
            });
            onScoreUpdate(result);
            onPhaseChange(nextPhase);
        } catch (e) {
            log.error("Failed to score MSK lite lifestyle assessment", {
                exception: e,
                lifestylePhase,
            });
        }
    };

    // Render based on phase from root
    if (phase === "movement") {
        return (
            <div className={COMPONENT_CLASS}>
                <div className={`user-menu hide-on-mobile`} style={{}}>
                    <AssessmentsUserMenu profileImage={false} />
                </div>
                <BaseCardLayout>
                    <MovementScreen
                        screenSettings={settings}
                        setScreenSettings={updateSettings}
                        settingsOnBack={onBackButtonClick}
                        onSubmit={handleMovementSubmit}
                    />
                </BaseCardLayout>
            </div>
        );
    }

    if (phase === "bodyComposition") {
        return (
            <div className={COMPONENT_CLASS}>
                <div className={`user-menu hide-on-mobile`} style={{}}>
                    <AssessmentsUserMenu profileImage={false} />
                </div>
                <BaseCardLayout>
                    <LifestyleScreen
                        key={phase}
                        screenSettings={settings}
                        setScreenSettings={updateSettings}
                        settingsOnBack={onBackButtonClick}
                        groupId="BODY_COMPOSITION"
                        onSubmit={() => handleLifestyleSubmit("bodyComposition")}
                    />
                </BaseCardLayout>
            </div>
        );
    }

    if (phase === "physicalActivity") {
        return (
            <div className={COMPONENT_CLASS}>
                <div className={`user-menu hide-on-mobile`} style={{}}>
                    <AssessmentsUserMenu profileImage={false} />
                </div>
                <BaseCardLayout>
                    <LifestyleScreen
                        key={phase}
                        screenSettings={settings}
                        setScreenSettings={updateSettings}
                        settingsOnBack={onBackButtonClick}
                        groupId="PHYSICAL_ACTIVITY"
                        onSubmit={() => handleLifestyleSubmit("physicalActivity")}
                    />
                </BaseCardLayout>
            </div>
        );
    }

    if (phase === "injuryHistory") {
        return (
            <div className={COMPONENT_CLASS}>
                <div className={`user-menu hide-on-mobile`} style={{}}>
                    <AssessmentsUserMenu profileImage={false} />
                </div>
                <BaseCardLayout>
                    <LifestyleScreen
                        screenSettings={settings}
                        setScreenSettings={updateSettings}
                        settingsOnBack={onBackButtonClick}
                        groupId="INJURY_HISTORY"
                        onSubmit={() => handleLifestyleSubmit("injuryHistory")}
                    />
                </BaseCardLayout>
            </div>
        );
    }

    if (phase === "finished") {
        return (
            <div className={COMPONENT_CLASS}>
                <div className={`user-menu hide-on-mobile`} style={{}}>
                    <AssessmentsUserMenu profileImage={false} />
                </div>
                <BaseCardLayout>
                    <div style={{ padding: "20px" }}>
                        <h1>MSK Lite Results</h1>
                        {currentScore && (
                            <div>
                                <p><strong>Overall Score:</strong> {currentScore.percentage ?? "N/A"}%</p>
                                <p><strong>Movement Score:</strong> {currentScore.movementScore?.percentage ?? "N/A"}%</p>
                                <p><strong>Body Comp Score:</strong> {currentScore.bodyCompositionScore?.percentage ?? "N/A"}%</p>
                                <p><strong>Physical Activity Score:</strong> {currentScore.physicalActivityScore?.percentage ?? "N/A"}%</p>
                                <p><strong>Injury History Score:</strong> {currentScore.injuryHistoryScore?.percentage ?? "N/A"}%</p>
                                {currentScore.riskFactor && (
                                    <>
                                        <p><strong>Risk Factor:</strong> {currentScore.riskFactor.riskCode}</p>
                                        <p><strong>Risk Percentage:</strong> {currentScore.riskFactor.percentage ?? "N/A"}%</p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </BaseCardLayout>
            </div>
        );
    }

    return null;
};

// ---------- Root MSKLite Page ----------
const MSKLitePage: React.FC = () => {
    const { state } = useAuthState();
    const [score, setScore] = useState<MskScoreLite | null>(null);
    const [phase, setPhase] = useState<MskLitePhase>("movement");

    if (!state.authenticated) {
        return <Redirect to="/login" />;
    }

    if (!state.user?.isSuperAdmin) {
        return <Redirect to="/" />;
    }

    if (!state.user?.id) {
        return <div>Loading...</div>;
    }

    const handlePhaseChange = (newPhase: MskLitePhase) => {
        log.debug("[MSKLite] Root: Phase changing to", { newPhase });
        setPhase(newPhase);
    };

    const handleScoreUpdate = (newScore: MskScoreLite) => {
        log.debug("[MSKLite] Root: Score updated", { newScore });
        setScore(newScore);
    };

    return (
        <>
            <NavigationDirectionProvider>
                <BackControllerProvider>
                    <MSKLiteInner
                        userId={state.user.id}
                        phase={phase}
                        onPhaseChange={handlePhaseChange}
                        currentScore={score}
                        onScoreUpdate={handleScoreUpdate}
                    />
                </BackControllerProvider>
            </NavigationDirectionProvider>
        </>
    );
};

export default MSKLitePage;